import { useState, useEffect, useCallback } from "react";
import { cn } from "../lib/utils";
import { Sword, Users, X } from "lucide-react";
import DOMPurify from 'dompurify';

// Outlined text component for fancy styling
const OutlinedText = ({ children, color = "yellow" }) => {
  const colorClasses = {
    yellow: "text-yellow-300",
    cyan: "text-cyan-300",
    white: "text-white",
    green: "text-green-300",
    red: "text-red-300",
    purple: "text-purple-300",
    orange: "text-orange-300",
  };
  
  return (
    <span 
      className={cn("font-black", colorClasses[color])}
      style={{ 
        textShadow: '2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 0 2px 0 #000, 0 -2px 0 #000, 2px 0 0 #000, -2px 0 0 #000'
      }}
    >
      {children}
    </span>
  );
};

const FightNotification = ({ notification, onDismiss, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(notification.id);
    }, 300);
  }, [notification.id, onDismiss]);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setIsVisible(true));
    
    // Auto dismiss after 6 seconds
    const timer = setTimeout(() => {
      handleDismiss();
    }, 6000);

    return () => clearTimeout(timer);
  }, [handleDismiss]);

  const isStartFight = notification.type === "start_fight";
  const isEnterWindow = notification.type === "enter_window";
  const isSpawned = notification.type === "spawned";
  const isAdminBroadcast = notification.type === "admin_broadcast";
  const isNewBounty = notification.type === "new_bounty";
  const isNewExpedition = notification.type === "new_expedition";
  const isBountyAccepted = notification.type === "bounty_accepted";
  const isBountyConfirmPending = notification.type === "bounty_confirm_pending";
  const isBountyCompleted = notification.type === "bounty_completed";
  const isExpeditionApplication = notification.type === "expedition_application";
  const isExpeditionAccepted = notification.type === "expedition_accepted";
  const isExpeditionRejected = notification.type === "expedition_rejected";
  const isHCGeneric = notification.type?.includes("expedition_") || notification.type?.includes("bounty_") || notification.type?.includes("admin_bounty") || notification.type?.includes("admin_expedition");
  const isSystem = isEnterWindow || isSpawned || isAdminBroadcast;
  const isHuntersChance = isNewBounty || isNewExpedition || isBountyAccepted || isBountyConfirmPending || isBountyCompleted || isExpeditionApplication || isExpeditionAccepted || isExpeditionRejected || isHCGeneric;

  let bgColor = "from-red-600/95 to-rose-700/95";
  let borderColor = "border-red-400";
  if (isStartFight) {
    bgColor = "from-green-600/95 to-emerald-700/95";
    borderColor = "border-green-400";
  } else if (isEnterWindow) {
    bgColor = "from-orange-500/95 to-orange-600/95";
    borderColor = "border-orange-400";
  } else if (isSpawned) {
    bgColor = "from-blue-600/95 to-blue-700/95";
    borderColor = "border-blue-400";
  } else if (isAdminBroadcast) {
    bgColor = "from-yellow-400/95 to-yellow-600/95";
    borderColor = "border-yellow-200";
  } else if (isNewBounty || isBountyAccepted || isBountyConfirmPending || isBountyCompleted) {
    bgColor = "from-amber-700/95 to-amber-900/95";
    borderColor = "border-amber-400";
  } else if (isNewExpedition || isExpeditionApplication || isExpeditionAccepted || isExpeditionRejected) {
    bgColor = "from-indigo-600/95 to-purple-700/95";
    borderColor = "border-indigo-400";
  }

  return (
    <div
      className={cn(
        "fixed left-1/2 -translate-x-1/2 z-50 transition-all duration-300",
        isVisible && !isLeaving ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-4 scale-95"
      )}
      style={{ top: `${80 + index * 110}px` }}
    >
      <div 
        className={cn(
          "bg-gradient-to-r shadow-2xl rounded-lg border-2 px-5 py-4 min-w-[420px] max-w-[580px] cursor-pointer relative",
          bgColor,
          borderColor
        )}
        onClick={handleDismiss}
      >
        {/* Close button */}
        <button 
          className="absolute top-2 right-2 text-white/60 hover:text-white"
          onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-4">
          {/* Boss Image */}
          {notification.bossImage && (
            <div className="w-16 h-16 rounded-lg bg-black/30 border-2 border-white/30 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img 
                src={notification.bossImage} 
                alt={notification.boss_name}
                className="w-full h-full object-contain p-1"
              />
            </div>
          )}

          {/* User Avatar - only for non-system notifications */}
          {!isSystem && (
            <div className="relative flex-shrink-0">
              <div className={cn(
                "w-14 h-14 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-2",
                isNewBounty ? "border-red-300/70" : isNewExpedition ? "border-indigo-300/70" : "border-white/50"
              )}>
                {notification.user_avatar ? (
                  <img
                    src={notification.user_avatar}
                    alt={notification.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Users className="w-7 h-7 text-white/80" />
                )}
              </div>
              {/* Guild Emblem Badge */}
              {notification.guild_emblem && (
                <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-black/50 border-2 border-white overflow-hidden">
                  <img
                    src={notification.guild_emblem}
                    alt={notification.guild_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 text-white min-w-0">
            {/* Header with icon */}
            <div className="flex items-center gap-2 mb-2">
              {!isSystem && !isHuntersChance && <Sword className={cn("w-5 h-5 text-white flex-shrink-0", isStartFight && "animate-bounce")} />}
              <span className={cn("text-sm font-bold uppercase tracking-wider", isSystem && "text-base")}>
                {isAdminBroadcast ? "Server Broadcast" :
                 isNewBounty ? "BOUNTY POSTED" :
                 isNewExpedition ? "EXPEDITION POSTED" :
                 isBountyAccepted ? "BOUNTY ACCEPTED" :
                 isBountyConfirmPending ? "AWAITING CONFIRMATION" :
                 isBountyCompleted ? "BOUNTY COMPLETED" :
                 isExpeditionApplication ? "NEW APPLICATION" :
                 isExpeditionAccepted ? "APPLICATION ACCEPTED" :
                 isExpeditionRejected ? "APPLICATION REJECTED" :
                 notification.type === "expedition_invite" ? "EXPEDITION INVITE" :
                 notification.type === "expedition_client_selected" ? "SELECTED AS CLIENT" :
                 notification.type === "expedition_expelled" ? "REMOVED FROM EXPEDITION" :
                 notification.type?.includes("admin_") ? "ADMIN ACTION" :
                 isStartFight ? "Fight Started!" :
                 isEnterWindow ? "Spawn Window Opened!" :
                 isSpawned ? "Boss Spawned!" :
                 "Boss Killed!"}
              </span>
            </div>
            
            {/* Main notification text */}
            <p className={cn("text-base leading-relaxed", isSystem && !isAdminBroadcast && "font-black drop-shadow-[0_2px_2px_rgba(0,0,0,1)]", isAdminBroadcast && "text-black drop-shadow-none")}>
              {isAdminBroadcast ? (
                <>
                  <span className="font-bold uppercase tracking-widest text-lg drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]">
                    {notification.username} ({notification.role}):
                  </span>
                  {" "}
                  <span
                    className="whitespace-pre-wrap text-lg"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notification.message) }}
                  />
                </>
              ) : isNewBounty ? (
                <>
                  <OutlinedText color="yellow">{notification.username}</OutlinedText>
                  <span className="text-white/90"> has </span>
                  <OutlinedText color="red">POSTED</OutlinedText>
                  <span className="text-white/90"> a </span>
                  <OutlinedText color="orange">BOUNTY</OutlinedText>
                  <span className="text-white/90"> for </span>
                  <OutlinedText color="green">{notification.boss_name}</OutlinedText>
                  {notification.reward && (
                    <>
                      <span className="text-white/90"> - Reward: </span>
                      <OutlinedText color="yellow">{notification.reward}</OutlinedText>
                    </>
                  )}
                </>
              ) : isNewExpedition ? (
                <>
                  <OutlinedText color="yellow">{notification.username}</OutlinedText>
                  <span className="text-white/90"> has </span>
                  <OutlinedText color="purple">POSTED</OutlinedText>
                  <span className="text-white/90"> an </span>
                  <OutlinedText color="cyan">EXPEDITION</OutlinedText>
                  <span className="text-white/90"> for </span>
                  <OutlinedText color="green">{notification.boss_name}</OutlinedText>
                </>
              ) : isBountyAccepted ? (
                <>
                  <OutlinedText color="yellow">{notification.username}</OutlinedText>
                  <span className="text-white/90"> has </span>
                  <OutlinedText color="green">ACCEPTED</OutlinedText>
                  <span className="text-white/90"> your </span>
                  <OutlinedText color="orange">BOUNTY</OutlinedText>
                  <span className="text-white/90"> for </span>
                  <OutlinedText color="red">{notification.boss_name}</OutlinedText>
                </>
              ) : isBountyConfirmPending ? (
                <>
                  <OutlinedText color="yellow">{notification.username}</OutlinedText>
                  <span className="text-white/90"> has </span>
                  <OutlinedText color="green">CONFIRMED</OutlinedText>
                  <span className="text-white/90"> the </span>
                  <OutlinedText color="orange">BOUNTY</OutlinedText>
                  <span className="text-white/90"> for </span>
                  <OutlinedText color="red">{notification.boss_name}</OutlinedText>
                  <span className="text-white/90"> - Your turn!</span>
                </>
              ) : isBountyCompleted ? (
                <>
                  <OutlinedText color="orange">BOUNTY</OutlinedText>
                  <span className="text-white/90"> for </span>
                  <OutlinedText color="green">{notification.boss_name}</OutlinedText>
                  <span className="text-white/90"> has been </span>
                  <OutlinedText color="yellow">COMPLETED</OutlinedText>
                  <span className="text-white/90">! Reputation awarded.</span>
                </>
              ) : isExpeditionApplication ? (
                <>
                  <OutlinedText color="yellow">{notification.username}</OutlinedText>
                  <span className="text-white/90"> has </span>
                  <OutlinedText color="cyan">APPLIED</OutlinedText>
                  <span className="text-white/90"> to your </span>
                  <OutlinedText color="purple">EXPEDITION</OutlinedText>
                  <span className="text-white/90"> for </span>
                  <OutlinedText color="green">{notification.boss_name}</OutlinedText>
                </>
              ) : isExpeditionAccepted ? (
                <>
                  <span className="text-white/90">You have been </span>
                  <OutlinedText color="green">ACCEPTED</OutlinedText>
                  <span className="text-white/90"> to the </span>
                  <OutlinedText color="purple">EXPEDITION</OutlinedText>
                  <span className="text-white/90"> for </span>
                  <OutlinedText color="cyan">{notification.boss_name}</OutlinedText>
                </>
              ) : isExpeditionRejected ? (
                <>
                  <span className="text-white/90">Your application to the </span>
                  <OutlinedText color="purple">EXPEDITION</OutlinedText>
                  <span className="text-white/90"> for </span>
                  <OutlinedText color="red">{notification.boss_name}</OutlinedText>
                  <span className="text-white/90"> was </span>
                  <OutlinedText color="red">DECLINED</OutlinedText>
                </>
              ) : isHCGeneric ? (
                <>
                  {notification.message ? (
                    <span className="text-white/90">{notification.message}</span>
                  ) : (
                    <>
                      <OutlinedText color="yellow">{notification.username}</OutlinedText>
                      <span className="text-white/90"> — </span>
                      <OutlinedText color="green">{notification.boss_name}</OutlinedText>
                    </>
                  )}
                </>
              ) : (
                <>
                  {!isSystem && (
                    <>
                      <OutlinedText color="yellow">{notification.username}</OutlinedText>
                      <span className="text-white/90">
                        {isStartFight ? " started a fight with " : " has killed the "}
                      </span>
                    </>
                  )}

                  <OutlinedText color="green">{notification.boss_name}</OutlinedText>

                  {isEnterWindow ? (
                    <>
                      <span className="text-white/90"> has entered </span>
                      <OutlinedText color="yellow">SPAWN WINDOW</OutlinedText>
                      <span className="text-white/90"> on </span>
                      <OutlinedText color="cyan">CHANNEL {notification.channel}</OutlinedText>
                      {notification.location_name && (
                        <>
                          <span className="text-white/90"> at </span>
                          <OutlinedText color="white">{notification.location_name}</OutlinedText>
                        </>
                      )}
                    </>
                  ) : isSpawned ? (
                    <>
                      <span className="text-white/90"> has for sure </span>
                      <OutlinedText color="yellow">spawned</OutlinedText>
                      <span className="text-white/90"> (Unless killed) </span>
                      {notification.location_name && (
                        <>
                          <span className="text-white/90">at </span>
                          <OutlinedText color="white">{notification.location_name}</OutlinedText>
                          <span className="text-white/90"> at </span>
                        </>
                      )}
                      <OutlinedText color="cyan">CHANNEL {notification.channel}</OutlinedText>
                    </>
                  ) : (
                    <>
                      <span className="text-white/90"> on </span>
                      <OutlinedText color="cyan">Channel {notification.channel}</OutlinedText>
                      {notification.location_name && (
                        <>
                          <span className="text-white/90"> in </span>
                          <span className="text-white font-semibold">{notification.location_name}</span>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </p>
            
            {/* Guild info */}
            {!isSystem && notification.guild_name && (
              <p className="text-white/60 text-xs mt-1.5">
                Guild: <span className="text-white/80">{notification.guild_name}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Container component to manage multiple notifications
const FightNotificationContainer = ({ notifications, onDismiss, getBossImage }) => {
  return (
    <>
      {notifications.slice(0, 4).map((notification, index) => (
        <FightNotification 
          key={notification.id}
          notification={notification} 
          onDismiss={onDismiss}
          index={index}
        />
      ))}
    </>
  );
};

export { FightNotification, FightNotificationContainer };
export default FightNotificationContainer;
